import { MESSAGES } from 'src/constants';

const { VCARD_FILENAME } = MESSAGES;

/* ***************************************************************************
 * contactCardAttachments — CON-1934
 *
 * The contact card in the multi-attachment beta, the same on every web
 * surface (Smart Action editors, the Connections composer, SEND TEXT, the
 * scheduled edit screens).
 *
 * The card is always attachment 1: it travels in the legacy single field and
 * the files behind it are the extras. So:
 *   - room left: "Add Contact Card" puts the card in front and moves every
 *     file already there one place back. Nothing is dropped.
 *   - full: "Replace with Contact Card" replaces attachment 1 only; the other
 *     files stay.
 *   - nothing attached, or outside the beta: the card is the one attachment,
 *     exactly as before.
 *
 * The card is the user's own blob, so a set with the card in it lives in the
 * user's own container; the server copies any file from another account's
 * container over on Save (definitionAttachments.unifyDefinitionContainer).
 *
 * It counts toward the MMS budget at its real size. Without a photo it is a
 * few hundred bytes; with the embedded contact card photo it is about 70 KB
 * (a third of Production cards, 13 Oct 2026). It is never compressed.
 *************************************************************************** */

export const isContactCardName = (name) => name === VCARD_FILENAME;

/* The container to read an attachment from: the card is always the user's
 * own (null makes helpers.azure.getImageFileURL use it), every other file the
 * message's. Every size and rebalance URL builder goes through this. */
export const containerForFile = (name, container) => (
  isContactCardName(name) ? null : container
);

/* Room for one more file beside a real (non-card) attachment 1. */
export const canAddContactCard = ({ first, extras = [], maxAttachments = 1 }) => (
  maxAttachments > 1
  && !!first
  && !isContactCardName(first)
  && (1 + extras.filter(Boolean).length) < maxAttachments
);

export const contactCardMenuLabel = ({ hasAttachment, canAdd }) => {
  if (!hasAttachment) return 'Attach Your Contact Card';
  return canAdd ? 'Add Contact Card' : 'Replace with Contact Card';
};

/* The attachments after the card is chosen. `added` says the old
 * attachment 1 moved behind the card rather than being replaced. */
export const applyContactCard = ({ first, extras = [], maxAttachments = 1 }) => {
  const rest = extras.filter(Boolean);
  if (!first || isContactCardName(first) || maxAttachments <= 1) {
    return { first: VCARD_FILENAME, extras: maxAttachments > 1 ? rest : [], added: false };
  }
  if (canAddContactCard({ first, extras: rest, maxAttachments })) {
    return { first: VCARD_FILENAME, extras: [first, ...rest], added: true };
  }
  return { first: VCARD_FILENAME, extras: rest, added: false };
};

/* Applying a message template replaces the message and its attachments.
 * In the beta, when that takes away files the user had attached, the
 * screen says so rather than dropping them quietly. Quoted in the KB. */
export const TEMPLATE_REPLACED_ATTACHMENTS_MESSAGE = 'Applying the template replaced your attachments.';

export const templateDropsAttachments = ({
  maxAttachments = 1, current = [], templateAttachment = null, templateExtras = [],
}) => {
  if (maxAttachments <= 1) return false;
  // CON-1936 — a template can bring files 2..N too; only a file it does not
  // bring is taken away.
  const kept = new Set([templateAttachment, ...(templateExtras || [])].filter(Boolean));
  return current.filter((name) => name && !kept.has(name)).length > 0;
};

/* CON-1936 — the files a message template brings into an editor: its
 * attachment 1 and, in the beta, its files 2..N (never the card, trimmed to
 * the limit). Outside the beta (a limit of 1) only attachment 1, exactly as
 * before. Every template shares one container (the template's). */
export const templateAttachmentsForApply = ({ template = null, maxAttachments = 1 }) => {
  const first = template?.attachment || null;
  if (!first || maxAttachments <= 1) return { first, extras: [] };
  const list = Array.isArray(template?.additionalAttachments) ? template.additionalAttachments : [];
  const extras = list
    .filter((name, i) => name && name !== first && !isContactCardName(name) && list.indexOf(name) === i)
    .slice(0, maxAttachments - 1);
  return { first, extras };
};
